// In-memory cache for Task Lists (Page 1 results per filter key)
// Enables instant zero-wait navigation with background stale-while-revalidate

const taskListCacheMap = new Map();
const inFlightTaskPromises = new Map();
const TTL_MS = 60 * 1000; // 60 seconds

const extractIds = (user) => {
  if (!user) return { userId: null, tenantId: null };
  const userId = user._id ? user._id.toString() : user.id ? user.id.toString() : null;
  const tenant = user.tenantId;
  const tenantId = tenant ? (typeof tenant === 'object' && tenant._id ? tenant._id.toString() : tenant.toString()) : null;
  return { userId, tenantId };
};

const makeKey = (userId, tenantId, filterKey) => {
  return `${tenantId || 'none'}:${userId || 'none'}:${filterKey}`;
};

export const getCachedTaskList = (user, filterKey) => {
  if (!user) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;

  const key = makeKey(userId, tenantId, filterKey);
  const entry = taskListCacheMap.get(key);
  if (!entry) return null;

  if (Date.now() - entry.timestamp < TTL_MS) {
    return entry.data;
  }
  return null;
};

export const setCachedTaskList = (user, filterKey, data) => {
  if (!user || !data) return;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return;

  const key = makeKey(userId, tenantId, filterKey);
  taskListCacheMap.set(key, {
    data,
    timestamp: Date.now(),
  });
};

export const fetchTasksCached = async (taskAPI, user, params, filterKey) => {
  if (!user || !taskAPI?.getAll) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;

  const key = makeKey(userId, tenantId, filterKey);
  const pageNum = params?.page || 1;
  const inFlightKey = `${key}:p_${pageNum}`;

  // Return in-flight promise if an identical request for the same page is already active
  if (inFlightTaskPromises.has(inFlightKey)) {
    return inFlightTaskPromises.get(inFlightKey);
  }

  const p = taskAPI.getAll(params)
    .then((res) => {
      const payload = res?.data?.data || res?.data;
      if (pageNum === 1) {
        setCachedTaskList(user, filterKey, payload);
      }
      return payload;
    })
    .catch((err) => {
      throw err;
    })
    .finally(() => {
      inFlightTaskPromises.delete(inFlightKey);
    });

  inFlightTaskPromises.set(inFlightKey, p);
  return p;
};

export const invalidateTaskListCache = () => {
  taskListCacheMap.clear();
  inFlightTaskPromises.clear();
};
